import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Modal from 'common/components/modal/Modal';
import Button from 'common/components/button/Button';
import CheckBox from 'common/components/checkBox/CheckBox';
import InputSearch from 'common/components/inputSearch/InputSearch';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import VehiclesService from 'api/vehicles/VehiclesService';
import DashboardService from 'api/dashboard/DashboardService';
import { VehicleFuelCatalogItem } from 'api/vehicles/models/VehicleFuelCatalogItem';
import Logger from 'common/services/Logger';
import { DefaultAssetImage, FUELS, LOGGER_LOG_TYPE } from 'Config';
import { FaCar, FaCheck, FaExclamationTriangle, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { IoSwapVertical } from 'react-icons/io5';
import { ReactComponent as FiltersIcon } from 'assets/svg/desktop_filtres.svg';
import { WIDGET_REGISTRY, ASSET_WIDGET_IDS, ASSET_GPS_DEPENDENT_WIDGET_IDS, ASSET_RESERVATION_WIDGET_IDS, widgetTitleKey, computeAssetAvailableWidgetIds } from '../widgetRegistry';
import useInfiniteReveal from './useInfiniteReveal';
import styles from './ReplicateLayoutModal.module.scss';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	// Ativo atual: excluído da lista e, no modo "Copiar de", é o destino da cópia.
	currentVehicleId: string;
	// Disposição atual (config JSON) — usada no modo "Replicar para".
	layoutJson: string;
	// Aplica a disposição de outro ativo a ESTE ativo (modo "Copiar de"). Recebe o item do
	// catálogo (com capacidades) para sanitizar contra as capacidades da origem.
	onCopyFrom: (source: VehicleFuelCatalogItem) => Promise<void>;
	// Este ativo já tem disposição (widgets visíveis)? Se NÃO (dashboard vazio), o modal abre
	// já com "Copiar de" escolhido (herdar de outro ativo sem ter de adicionar widgets primeiro).
	// "Replicar para" continua SEMPRE disponível (ex.: replicar a vazio = dar reset a vários ativos).
	hasLayout: boolean;
	// Mostrar a coluna "Localizador GPS" — só quando o utilizador tem a policy de GPS.
	showGpsColumn?: boolean;
	// Capacidades do ativo ATUAL (destino no "Copiar de") — para o aviso de incompatibilidade.
	currentAssetCaps?: AssetCaps;
};

// Critério: "Copiar de" (1 origem → este ativo) | "Replicar para" (este ativo → N destinos).
type Mode = 'from' | 'to';

type SortCol = 'label' | 'brand' | 'model' | 'reservable' | 'gps';

const PAGE_SIZE = 25;

const normalize = (s: string) => (s || '').toLowerCase().trim();

// Altura FIXA → o modal não salta de tamanho ao pesquisar (a tabela é que rola por dentro).
const MODAL_STYLE = {
	content: {
		position: 'relative' as const,
		inset: 'auto',
		width: 'min(980px, 96vw)',
		height: 'min(840px, 92vh)',
		padding: 0,
		overflow: 'hidden',
		display: 'flex',
	},
};

// Separador de secção reutilizável (ícone + título a bold + linha), no estilo dos
// cabeçalhos de secção da app (ReminderTriggerForm). Usado em "Por combustível" e "Ativos".
const SectionSeparator = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
	<div className={styles.sectionHeader}>
		{icon}
		<span className={styles.sectionHeaderTitle}>{title}</span>
		<span className={styles.sectionHeaderLine} />
	</div>
);

// Capacidades de um ativo que condicionam que widgets consegue mostrar.
type AssetCaps = { hasGps?: boolean; reservationAvailable?: boolean; hasContractedKm?: boolean; hasKmFeature?: boolean; fuels?: string[] };

type IncompatGroup = { reason: 'gps' | 'reservation' | 'contract_km' | 'fuel'; widgets: string[] };

// Dado um conjunto de widgets de origem e as capacidades do ativo DESTINO, devolve os widgets
// que NÃO vão aparecer, agrupados por motivo. Mesmas regras do gate do picker (CarDashboardTab).
const computeIncompatible = (srcIds: string[], caps: AssetCaps): IncompatGroup[] => {
	const groups: IncompatGroup[] = [];
	if (!caps.hasGps) {
		const w = srcIds.filter(id => ASSET_GPS_DEPENDENT_WIDGET_IDS.includes(id));
		if (w.length) groups.push({ reason: 'gps', widgets: w });
	}
	if (!caps.reservationAvailable) {
		const w = srcIds.filter(id => ASSET_RESERVATION_WIDGET_IDS.includes(id));
		if (w.length) groups.push({ reason: 'reservation', widgets: w });
	}
	if (!caps.hasContractedKm) {
		const w = srcIds.filter(id => id === 'contractedKmAlerts');
		if (w.length) groups.push({ reason: 'contract_km', widgets: w });
	}
	const fuels = caps.fuels ?? [];
	const hasElectric = fuels.includes(FUELS.ELECTRIC);
	const hasCombustion = fuels.some(f => !!f && f !== FUELS.ELECTRIC);
	const fuelWidgets: string[] = [];
	if (!hasCombustion && srcIds.includes('fuelConsumptionPerAsset')) fuelWidgets.push('fuelConsumptionPerAsset');
	if (!hasElectric && srcIds.includes('electricConsumptionPerAsset')) fuelWidgets.push('electricConsumptionPerAsset');
	if ((fuels.length < 2 || !caps.hasKmFeature) && srcIds.includes('consumesChart')) fuelWidgets.push('consumesChart');
	if (fuelWidgets.length) groups.push({ reason: 'fuel', widgets: fuelWidgets });
	return groups;
};

const catalogCaps = (a: VehicleFuelCatalogItem): AssetCaps => ({
	hasGps: a.hasGps,
	reservationAvailable: a.reservationAvailable,
	hasContractedKm: a.hasContractedKm,
	hasKmFeature: a.hasKmFeature,
	fuels: a.fuelsList,
});

// "Copiar de" (por linha): widgets que a ORIGEM consegue mostrar mas o ativo ATUAL (destino) NÃO.
// Aproximação por capacidades (sem ler a disposição de cada carro): só perdas REAIS — copiar
// gasóleo→gasolina não perde nada de combustível; copiar um c/ GPS p/ um sem GPS perde os de GPS.
const copyIncompatible = (sourceCaps: AssetCaps, currentCaps: AssetCaps): IncompatGroup[] => {
	const all = Array.from(ASSET_WIDGET_IDS);
	const sourceHidden = new Set(computeIncompatible(all, sourceCaps).flatMap(g => g.widgets));
	return computeIncompatible(all, currentCaps)
		.map(g => ({ ...g, widgets: g.widgets.filter(id => !sourceHidden.has(id)) }))
		.filter(g => g.widgets.length > 0);
};

const ReplicateLayoutModal = ({ isOpen, onClose, currentVehicleId, layoutJson, onCopyFrom, hasLayout, showGpsColumn, currentAssetCaps }: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [mode, setMode] = useState<Mode | null>(null);
	const [assets, setAssets] = useState<VehicleFuelCatalogItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isApplying, setIsApplying] = useState(false);
	const [search, setSearch] = useState('');
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [sortCol, setSortCol] = useState<SortCol>('label');
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
	const imageCacheKey = useRef(new Date().getTime());
	// "Copiar de": cache por-ativo da disposição da ORIGEM (fetch lazy no hover/seleção) →
	// incompatibilidades exatas + contador "X de N". Evita fetch em massa.
	const [copyData, setCopyData] = useState<Record<string, { shown: number; total: number; incompat: IncompatGroup[] }>>({});

	// No modo "Copiar de" só se escolhe UM ativo (origem) e os combustíveis ficam ocultos.
	const singleSelect = mode === 'from';

	useEffect(() => {
		if (!isOpen) return;
		let cancelled = false;
		// Vazio → abre já em "Copiar de" (para herdar); com disposição → pede para escolher critério.
		setMode(hasLayout ? null : 'from');
		setSearch('');
		setSelected(new Set());
		setSortCol('label');
		setSortDir('asc');
		setCopyData({});
		const load = async () => {
			try {
				setIsLoading(true);
				const result = await VehiclesService.catalogWithFuel();
				if (cancelled) return;
				setAssets((result || []).filter(a => a.value !== currentVehicleId));
			} catch (error) {
				addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error loading vehicles for replicate', error);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		load();
		return () => { cancelled = true; };
		// eslint-disable-next-line
	}, [isOpen, currentVehicleId, hasLayout]);

	// "Copiar de": busca a disposição da origem (1x, em cache) e calcula incompatibilidades
	// EXATAS + quantos widgets vão mesmo aparecer neste ativo. Chamado no hover e na seleção.
	// O total é o que a origem EFETIVAMENTE mostra (disposição gravada ∩ capacidades DELA) —
	// não a disposição crua: um ativo pode ter 7 widgets gravados mas só mostrar 1 (resto
	// escondido por falta de GPS, etc.). Espelha o que handleCopyFrom realmente copia.
	const ensureCopyData = (a?: VehicleFuelCatalogItem) => {
		if (!a || copyData[a.value]) return;
		DashboardService.getAssetLayout(a.value).then(res => {
			let visible: string[] = [];
			if (res?.layout) {
				try {
					const cfg = JSON.parse(res.layout);
					visible = ((cfg.widgets || []) as Array<{ i: string; visible?: boolean }>).filter(w => w.visible).map(w => w.i);
				} catch { /* layout inválido → sem widgets */ }
			}
			const sourceAvailable = computeAssetAvailableWidgetIds({
				hasGps: a.hasGps, reservationAvailable: a.reservationAvailable,
				hasKmFeature: a.hasKmFeature, hasContractedKm: a.hasContractedKm, fuelNames: a.fuelsList,
			});
			const effective = visible.filter(id => sourceAvailable.has(id));
			const inc = computeIncompatible(effective, currentAssetCaps ?? {});
			const hidden = new Set(inc.flatMap(g => g.widgets)).size;
			setCopyData(prev => (prev[a.value] ? prev : { ...prev, [a.value]: { incompat: inc, shown: Math.max(0, effective.length - hidden), total: effective.length } }));
		}).catch(() => { /* ignora */ });
	};

	// No "Copiar de", garante o cache da origem selecionada (para o resumo do rodapé).
	useEffect(() => {
		if (mode === 'from') ensureCopyData(assets.find(a => a.value === Array.from(selected)[0]));
		// eslint-disable-next-line
	}, [mode, selected, currentAssetCaps]);

	const selectMode = (m: Mode) => {
		setMode(m);
		setSelected(new Set());
	};

	const fuelLabel = (name: string) => {
		const key = `vehicle.fuels.${name}`;
		const label = t(key as any) as string;
		return label === key ? name : label;
	};

	const fuelText = (a: VehicleFuelCatalogItem) => (a.fuelsList || []).map(fuelLabel).join(', ');

	// Célula de capacidade (Reservável / GPS): ✓ teal quando tem, traço cinza quando não.
	const capCell = (has?: boolean) => (has
		? <FaCheck className={styles.capYes} />
		: <span className={styles.capNo}>—</span>);

	// Widgets visíveis na disposição de ORIGEM (a que se vai replicar).
	const sourceWidgetIds = useMemo(() => {
		try {
			const cfg = JSON.parse(layoutJson);
			return ((cfg.widgets || []) as Array<{ i: string; visible?: boolean }>)
				.filter(w => w.visible).map(w => w.i);
		} catch {
			return [] as string[];
		}
	}, [layoutJson]);

	// Resumo "X de N" para o popover (Replicar): N = widgets visíveis na origem; ocultos = únicos.
	const replicatedSummary = (groups: IncompatGroup[]) => {
		const total = sourceWidgetIds.length;
		const hidden = new Set(groups.flatMap(g => g.widgets)).size;
		return { shown: Math.max(0, total - hidden), total };
	};

	const widgetTitle = (id: string) => {
		const def = WIDGET_REGISTRY[id];
		return def ? (t(widgetTitleKey(def, true) as any) as string) : id;
	};

	const assetImage = (a: VehicleFuelCatalogItem) => {
		if (a.photoUrl) return `${a.photoUrl}?_=${imageCacheKey.current}`;
		if (a.vehicleSegmentDefaultPhotoUrl) return `${a.vehicleSegmentDefaultPhotoUrl}?_=${imageCacheKey.current}`;
		return DefaultAssetImage;
	};

	// Tipos de combustível presentes na frota → pills de atalho (marca todos os ativos desse combustível).
	const fuelTypes = useMemo(() => {
		const map = new Map<string, string[]>();
		assets.forEach(a => (a.fuelsList || []).forEach(f => {
			const list = map.get(f) || [];
			list.push(a.value);
			map.set(f, list);
		}));
		return Array.from(map.entries())
			.map(([name, ids]) => ({ name, ids }))
			.sort((x, y) => fuelLabel(x.name).localeCompare(fuelLabel(y.name)));
		// eslint-disable-next-line
	}, [assets]);

	const filteredAssets = useMemo(() => {
		const q = normalize(search);
		if (!q) return assets;
		return assets.filter(a =>
			normalize(a.label || '').includes(q)
			|| normalize(a.vehicleBrandName || '').includes(q)
			|| normalize(a.vehicleModelName || '').includes(q));
	}, [assets, search]);

	const sortedAssets = useMemo(() => {
		const value = (a: VehicleFuelCatalogItem) => {
			if (sortCol === 'brand') return a.vehicleBrandName || '';
			if (sortCol === 'model') return a.vehicleModelName || '';
			// Booleanos: '0' (tem) ordena antes de '1' (não tem) → ascendente mostra o ✓ primeiro.
			if (sortCol === 'reservable') return a.reservationAvailable ? '0' : '1';
			if (sortCol === 'gps') return a.hasGps ? '0' : '1';
			return a.label || '';
		};
		const dir = sortDir === 'asc' ? 1 : -1;
		return [...filteredAssets].sort((x, y) => value(x).localeCompare(value(y)) * dir);
		// eslint-disable-next-line
	}, [filteredAssets, sortCol, sortDir]);

	const filteredIds = useMemo(() => filteredAssets.map(a => a.value), [filteredAssets]);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(sortedAssets, PAGE_SIZE);

	// "Copiar de": pré-carrega (lazy, só as linhas reveladas que PODEM perder widgets por
	// capacidade) a disposição da origem, para o ⚠ só aparecer onde há perda REAL — uma origem
	// pode ter a capacidade (ex.: contrato com km) mas não ter esse widget visível. ensureCopyData
	// é idempotente (cache) → só busca cada origem uma vez.
	useEffect(() => {
		if (mode !== 'from' || !currentAssetCaps) return;
		visibleItems.forEach(a => {
			if (copyIncompatible(catalogCaps(a), currentAssetCaps).length > 0) ensureCopyData(a);
		});
		// eslint-disable-next-line
	}, [mode, visibleItems, currentAssetCaps]);

	const onSort = (col: SortCol) => {
		if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
		else { setSortCol(col); setSortDir('asc'); }
	};
	// Mesmo visual de ordenação das listagens do site (DataTable): IoSwapVertical (ordenável),
	// FaSortAmountDown (asc ativo), FaSortAmountUp (desc ativo).
	const sortIcon = (col: SortCol) => {
		if (sortCol !== col) return <IoSwapVertical className={styles.headerSortIcon} />;
		return sortDir === 'asc'
			? <FaSortAmountDown className={`${styles.headerSortIcon} ${styles.active}`} />
			: <FaSortAmountUp className={`${styles.headerSortIcon} ${styles.active}`} />;
	};

	const toggleAsset = (id: string) => {
		setSelected(prev => {
			if (singleSelect) {
				return prev.has(id) ? new Set<string>() : new Set([id]);
			}
			const next = new Set(prev);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	};

	const toggleMany = (ids: string[]) => {
		setSelected(prev => {
			const next = new Set(prev);
			const allIn = ids.length > 0 && ids.every(id => next.has(id));
			ids.forEach(id => { if (allIn) next.delete(id); else next.add(id); });
			return next;
		});
	};

	const isAllSelected = (ids: string[]) => ids.length > 0 && ids.every(id => selected.has(id));

	const apply = async () => {
		if (selected.size === 0 || !mode) return;
		try {
			setIsApplying(true);
			if (mode === 'from') {
				const src = assets.find(a => a.value === Array.from(selected)[0]);
				if (!src) return;
				await onCopyFrom(src);
				addToast(t('dashboard.replicate.success_from') as string, { appearance: 'success' });
			} else {
				await DashboardService.replicateAssetLayout(layoutJson, Array.from(selected));
				addToast(t('dashboard.replicate.success', { count: selected.size }) as string, { appearance: 'success' });
			}
			onClose();
		} catch (error) {
			addToast(t(mode === 'from' ? 'dashboard.replicate.error_from' : 'dashboard.replicate.error') as string, { appearance: 'error' });
			Logger.error(LOGGER_LOG_TYPE.DASHBOARD, 'error applying asset layout', error);
		} finally {
			setIsApplying(false);
		}
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={onClose} style={MODAL_STYLE}>
			<div className={styles.container}>
				<div className={styles.header}>
					<div className={styles.title}>{t('dashboard.replicate.title')}</div>
					<div className={styles.subtitle}>{t('dashboard.replicate.subtitle')}</div>
				</div>

				<div className={styles.criteriaRow}>
					<span className={styles.criteriaLabel}>{t('dashboard.replicate.criteria_label')}</span>
					<div className={styles.criteriaPills}>
						<button
							type="button"
							className={`${styles.criteriaPill} ${mode === 'from' ? styles.criteriaPillActive : ''}`}
							onClick={() => selectMode('from')}
						>
							{t('dashboard.replicate.criteria_from')}
						</button>
						<button
							type="button"
							className={`${styles.criteriaPill} ${mode === 'to' ? styles.criteriaPillActive : ''}`}
							onClick={() => selectMode('to')}
						>
							{t('dashboard.replicate.criteria_to')}
						</button>
					</div>
				</div>

				<div className={styles.body}>
					{mode === null ? (
						<div className={styles.choosePlaceholder}>{t('dashboard.replicate.choose_hint')}</div>
					) : (
						<>
							{mode === 'to' && fuelTypes.length > 0 && (
								<div className={styles.fuelRow}>
									<SectionSeparator
										icon={<FiltersIcon className={styles.sectionHeaderIcon} />}
										title={t('dashboard.replicate.by_fuel') as string}
									/>
									<div className={styles.fuelPills}>
										{fuelTypes.map(f => (
											<button
												key={f.name}
												type="button"
												className={`${styles.fuelPill} ${isAllSelected(f.ids) ? styles.active : ''}`}
												onClick={() => toggleMany(f.ids)}
											>
												{fuelLabel(f.name)}
											</button>
										))}
									</div>
								</div>
							)}

							<div className={styles.searchRow}>
								<SectionSeparator
									icon={<FaCar className={styles.sectionHeaderIcon} />}
									title={t('dashboard.replicate.assets') as string}
								/>
								<InputSearch placeholder={t('dashboard.replicate.search') as string} onChangeValue={setSearch} />
							</div>

							<div className={styles.tableWrapper}>
								{isLoading ? (
									<div className={styles.center}><LoadingSpinner /></div>
								) : filteredAssets.length === 0 ? (
									<div className={styles.empty}>{t('dashboard.replicate.no_assets')}</div>
								) : (
									<table className={styles.table}>
										<thead>
											<tr>
												<th className={styles.checkCol}>
													{!singleSelect && (
														<CheckBox checked={isAllSelected(filteredIds)} onChange={() => toggleMany(filteredIds)} />
													)}
												</th>
												<th className={styles.imageCol}>{t('common.image')}</th>
												<th className={styles.sortable} onClick={() => onSort('label')}>{sortIcon('label')}{t('vehicle.registration')}</th>
												<th className={styles.sortable} onClick={() => onSort('brand')}>{sortIcon('brand')}{t('vehicle.brand')}</th>
												<th className={styles.sortable} onClick={() => onSort('model')}>{sortIcon('model')}{t('vehicle.model')}</th>
												<th>{t('dashboard.replicate.col_fuel')}</th>
												<th className={`${styles.capCol} ${styles.sortable}`} onClick={() => onSort('reservable')}>{sortIcon('reservable')}{t('dashboard.replicate.col_reservable')}</th>
												{showGpsColumn && <th className={`${styles.capCol} ${styles.sortable}`} onClick={() => onSort('gps')}>{sortIcon('gps')}{t('dashboard.replicate.col_gps')}</th>}
											</tr>
										</thead>
										<tbody>
											{visibleItems.map(a => {
												// Replicar: o que não aparece no destino (esta linha). Copiar de: o que a
												// ORIGEM (esta linha) consegue mostrar mas o ativo atual NÃO.
												const toIncompat = mode === 'to' ? computeIncompatible(sourceWidgetIds, catalogCaps(a)) : [];
												const cached = mode === 'from' ? copyData[a.value] : undefined;
												// Replicar: disposição atual conhecida → exato já. Copiar de: ⚠ só quando o cache
												// confirma perda REAL (a capacidade só dispara o pré-carregamento, não o ⚠).
												const triangleVisible = mode === 'to' ? toIncompat.length > 0 : (!!cached && cached.incompat.length > 0);
												const incompat = mode === 'to' ? toIncompat : (cached ? cached.incompat : []);
												const summary = mode === 'to'
													? replicatedSummary(toIncompat)
													: (cached ? { shown: cached.shown, total: cached.total } : null);
												const summaryKey = mode === 'to' ? 'dashboard.replicate.incompatible_summary_to' : 'dashboard.replicate.incompatible_summary_from';
												return (
													<tr key={a.value} className={styles.row} onClick={() => toggleAsset(a.value)}>
														<td className={styles.checkCol} onClick={e => e.stopPropagation()}>
															<CheckBox checked={selected.has(a.value)} onChange={() => toggleAsset(a.value)} />
														</td>
														<td className={styles.imageCol}>
															<div className={styles.thumb} style={{ backgroundImage: `url(${assetImage(a)})` }} />
														</td>
														<td>
															<div className={styles.plateCell}>
																<span className={styles.plate}>{a.label}</span>
																{triangleVisible && (
																	<span className={styles.warnOverflow} onClick={e => e.stopPropagation()}>
																		<FaExclamationTriangle className={styles.warnIcon} />
																		<div className={styles.warnPopover}>
																			{incompat.length > 0 && <div className={styles.warnTitle}>{t((mode === 'to' ? 'dashboard.replicate.incompatible_title_to' : 'dashboard.replicate.incompatible_title_from') as any) as string}</div>}
																			{incompat.map(g => (
																				<div key={g.reason} className={styles.warnGroup}>
																					<div className={styles.warnReason}>{t(`dashboard.replicate.incompatible_${g.reason}` as any)}</div>
																					<ul className={styles.warnList}>
																						{g.widgets.map(id => <li key={id}>{widgetTitle(id)}</li>)}
																					</ul>
																				</div>
																			))}
																			{summary && <div className={`${styles.warnSummary} ${incompat.length > 0 ? styles.divided : ''}`}>{t(summaryKey as any, summary) as string}</div>}
																		</div>
																	</span>
																)}
															</div>
														</td>
														<td className={styles.muted}>{a.vehicleBrandName || '—'}</td>
														<td className={styles.muted}>{a.vehicleModelName || '—'}</td>
														<td className={styles.muted}>{fuelText(a) || '—'}</td>
														<td className={styles.capCol}>{capCell(a.reservationAvailable)}</td>
														{showGpsColumn && <td className={styles.capCol}>{capCell(a.hasGps)}</td>}
													</tr>
												);
											})}
											{hasMore && (
												<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
													<td colSpan={showGpsColumn ? 8 : 7} className={styles.sentinel} />
												</tr>
											)}
										</tbody>
									</table>
								)}
							</div>
						</>
					)}
				</div>

				<div className={styles.footer}>
					<div className={styles.footerInfo}>
						{selected.size > 0 && (
							mode === 'from' ? (
								<>
									{copyData[Array.from(selected)[0]] && <strong>{t('dashboard.replicate.incompatible_summary_from', copyData[Array.from(selected)[0]]) as string}</strong>}
									<span className={styles.warning}>{t('dashboard.replicate.overwrite_warning_from')}</span>
								</>
							) : (
								<>
									<strong>{t('dashboard.replicate.selected', { count: selected.size })}</strong>
									<span className={styles.warning}>{t('dashboard.replicate.overwrite_warning')}</span>
								</>
							)
						)}
					</div>
					<div className={styles.footerButtons}>
						<Button preset="secondary" text={t('common.cancel') as string} onClick={onClose} />
						<Button
							text={t('common.apply') as string}
							onClick={apply}
							isDisabled={selected.size === 0}
							isLoading={isApplying}
						/>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ReplicateLayoutModal;
